import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
    FaBell, 
    FaCalendarAlt, 
    FaUsers, 
    FaBuilding, 
    FaUser, 
    FaSearch,
    FaTimes,
    FaEye,
    FaEdit,
    FaTrash,
    FaCheckCircle
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './ManagerNotices.css';

const ManagerNotices = () => {
    const baseUrl = "https://api.burrayhr.com";
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [notices, setNotices] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedNotice, setSelectedNotice] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [updateFormData, setUpdateFormData] = useState({
        title: '',
        content: '',
        recipient_type: '',
        recipient_id: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchNotices();
    }, []);

    const fetchNotices = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/notices/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            console.log(response.data);
            setNotices(response.data);
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch notices'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const filteredNotices = notices.filter(notice =>
        notice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notice.content.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getRecipientIcon = (type) => {
        const icons = {
            all: <FaUsers className="recipient-icon all" />,
            department: <FaBuilding className="recipient-icon department" />,
            individual: <FaUser className="recipient-icon individual" />
        };
        return icons[type] || <FaUsers className="recipient-icon" />;
    };

    const getRecipientText = (notice) => {
        const types = {
            all: 'All Employees',
            department: `Department: ${notice.department_name || 'N/A'}`,
            individual: Array.isArray(notice.recipients_data) && notice.recipients_data.length > 0
                ? `Employees: ${notice.recipients_data.map(emp => emp.name).join(', ')}`
                : `Employee: ${notice.employee?.first_name} ${notice.employee?.last_name || 'N/A'}`
        };
        return types[notice.recipient_type] || 'Unknown Recipient';
    };

    const handleNoticeClick = (notice) => {
        setSelectedNotice(notice);
        
        Swal.fire({
            title: notice.title,
            html: `
                <div class="notice-info">
                    <div class="info-item">
                        <i class="fas fa-calendar-alt"></i>
                        <span>Posted on: ${formatDate(notice.created_at)}</span>
                    </div>
                    <div class="info-item">
                        <span>${getRecipientText(notice)}</span>
                    </div>
                </div>
                <div class="notice-content">
                    <p>${notice.content}</p>
                </div>
            `,
            showCloseButton: true,
            showDenyButton: true,
            showCancelButton: true,
            denyButtonText: 'Edit',
            cancelButtonText: 'Delete',
            denyButtonColor: '#1e3a8a',
            cancelButtonColor: '#dc2626',
            showConfirmButton: false
        }).then((result) => {
            if (result.isDenied) {
                handleUpdateClick(notice);
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                handleDeleteClick(notice);
            }
        });
    };

    const handleUpdateClick = (notice) => {
        Swal.fire({
            title: 'Update Notice',
            html: `
            
                <div class="notice-form-container">
                    <h3>Update Notice</h3>
                <form id="update-notice-form" class="notice-form">
                    <div class="form-group">
                        <input
                            type="text"
                            id="title"
                            class="swal2-input"
                            placeholder="Notice Title"
                            value="${notice.title}"
                        />
                    </div>
                    <div class="form-group">
                        <textarea
                            id="content"
                            class="swal2-textarea"
                            placeholder="Notice Content"
                            rows="6"
                        >${notice.content}</textarea>
                    </div>
                </form>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Update',
            confirmButtonColor: '#1e3a8a',
            cancelButtonColor: '#6c757d',
            preConfirm: () => {
                const title = document.getElementById('title').value;
                const content = document.getElementById('content').value;
                if (!title || !content) {
                    Swal.showValidationMessage('Please fill in all fields');
                    return false;
                }
                return { title, content };
            }
        }).then((result) => {
            if (result.isConfirmed) {
                handleUpdateNotice(notice.id, result.value);
            }
        });
    };

    const handleDeleteClick = (notice) => {
        Swal.fire({
            title: 'Delete Notice',
            text: 'Are you sure you want to delete this notice?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#1e3a8a',
            cancelButtonColor: '#dc2626',
            confirmButtonText: 'Yes, delete it'
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeleteNotice(notice.id);
            }
        });
    };

    const handleUpdateNotice = async (noticeId, updateData) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${baseUrl}/api/notices/${noticeId}/`, updateData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            fetchNotices();
            Swal.fire({
                icon: 'success',
                title: 'Notice Updated',
                timer: 2000,
                timerProgressBar: true
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Update Failed',
                text: error.response?.data?.message || 'Failed to update notice'
            });
        }
    };

    const handleDeleteNotice = async (noticeId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${baseUrl}/api/notices/${noticeId}/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            fetchNotices();
            Swal.fire({
                icon: 'success',
                title: 'Notice Deleted',
                timer: 2000,
                timerProgressBar: true
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Delete Failed',
                text: error.response?.data?.message || 'Failed to delete notice'
            });
        }
    };

    const handleViewReadStatuses = (notice, e) => {
        if (e) e.stopPropagation();
        
        const readStatuses = notice.read_statuses || [];
        const formattedDate = (dateString) => {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        };

        Swal.fire({
            title: 'Notice Read Status',
            html: `
                <div class="read-status-container">
                    <div class="read-status-header">
                        <h3>${notice.title}</h3>
                    </div>
                    <div class="read-status-list">
                        ${readStatuses.length > 0 ? 
                            readStatuses.map(status => `
                                <div class="read-status-item">
                                    <div class="employee-info">
                                        <FaCheckCircle className="read-icon" />
                                        <span class="employee-name">${status.employee_name}</span>
                                        ${status.message ? `<span class="employee-email">${status.message}</span>` : ''}
                                    </div>
                                </div>
                            `).join('') :
                            '<div class="no-read-status">No employees have read this notice yet</div>'
                        }
                    </div>
                </div>
            `,
            showConfirmButton: true,
            confirmButtonText: 'Close',
            confirmButtonColor: '#1e3a8a',
            customClass: {
                container: 'read-status-modal',
                popup: 'read-status-popup',
                title: 'read-status-title',
                htmlContainer: 'read-status-content',
                confirmButton: 'read-status-close-btn'
            }
        });
    };

    return (
        <div>
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="notices-container">
                    <div className="notices-header">
                        <div className="title-section">
                            <FaBell className="title-icon" />
                            <h1>Notices</h1>
                        </div>
                        <div className="search-section">
                            <div className="search-box">
                                <FaSearch className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search notices..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading-container">
                            <div className="spinner"></div>
                            <p>Loading notices...</p>
                        </div>
                    ) : filteredNotices.length === 0 ? (
                        <div className="no-notices">
                            <FaBell className="no-notices-icon" />
                            <h3>No Notices Found</h3>
                            <p>There are no notices matching your search criteria.</p>
                        </div>
                    ) : (
                        <div className="notices-grid">
                            {filteredNotices.map(notice => (
                                <div 
                                    key={notice.id} 
                                    className="notice-card"
                                    onClick={() => handleNoticeClick(notice)}
                                >
                                    <div className="notice-header">
                                        <div className="notice-title">
                                            <h3>{notice.title}</h3>
                                            <div className="notice-actions">
                                                <button 
                                                    style={{backgroundColor: 'lightgray', color: 'white !important', borderRadius: '0.5rem', border: 'none', padding: '0.5rem 1rem'}}
                                                    onClick={(e) => handleViewReadStatuses(notice, e)}
                                                    title="View Read Status"
                                                >
                                                    <span className="view-details">
                                                    <FaEye /> Views {notice.read_statuses?.length || 0}
                                                </span>
                                                </button>
                                                
                                            </div>
                                        </div>
                                        <div className="notice-meta">
                                            <div className="recipient-type">
                                                {getRecipientIcon(notice.recipient_type)}
                                                <span>{getRecipientText(notice)}</span>
                                            </div>
                                            <div className="notice-date">
                                                <FaCalendarAlt />
                                                <span>{formatDate(notice.created_at)}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="notice-preview">
                                        <p>{notice.content.substring(0, 150)}...</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManagerNotices;

const styles = `
    .notice-actions {
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    .view-status-btn {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        background-color: #f1f5f9;
        border: none;
        border-radius: 0.5rem;
        color: #fff;
        font-size: 0.875rem;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .view-status-btn:hover {
        background-color: #e2e8f0;
    }

    .read-status-container {
        text-align: left;
        max-height: 60vh;
        overflow-y: auto;
    }

    .read-status-header {
        margin-bottom: 1.5rem;
        padding-bottom: 1rem;
        border-bottom: 1px solid #e2e8f0;
    }

    .read-status-header h3 {
        color:rgb(221, 228, 247);
        margin-bottom: 0.5rem;
    }

    .read-status-count {
        color: #64748b;
        font-size: 0.875rem;
    }

    .read-status-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .read-status-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.75rem;
        background-color: #f8fafc;
        border-radius: 0.5rem;
    }

    .employee-info {
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }

    .read-icon {
        color: #10b981;
        font-size: 1rem;
    }

    .employee-name {
        font-weight: 500;
        color: #1e293b;
    }

    .read-date {
        font-size: 0.875rem;
        color: #64748b;
    }

    .no-read-status {
        text-align: center;
        padding: 2rem;
        color: #64748b;
        background-color: #f8fafc;
        border-radius: 0.5rem;
    }

    .read-status-modal .swal2-popup {
        max-width: 600px;
        width: 90%;
    }

    .read-status-content {
        margin: 1rem 0;
    }
`;